import { Grid } from '@mui/material';
import './ibp-obs.scss';
import { useEffect, useState } from 'react';
import SegmentedBarChart from 'components/Widgets/SegmentedBarChart';
import ChartWidget from 'components/Widgets/ChartWidget';
import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import IBPExecutiveDashboardIcon from '../../assets/material-symbols_table-chart-view-sharp.svg';

// Color map outside the function
const marketWiseColorMap: Record<string, string> = {
	Benelux: '#1976d2',   // Blue
	FLNA: '#ff9800',      // Orange
	France: '#388e3c',    // Green
	PBNA: '#03a9f4',      // Light Blue
	Turkey: '#ab47bc',    // Purple
	Serbia: '#8bc34a'   // Light Green 
};

// marketWiseData in new format
const marketWiseData = [
  {
    monthYear: 'Dec 2024',
    values: [
      { label: 'Turkey', value: 482 }
    ]
  },
  {
    monthYear: 'Mar 2025',
    values: [
      { label: 'France', value: 297 }
    ]
  },
  {
    monthYear: 'Jun 2025',
    values: [
      { label: 'FLNA', value: 121 },
      { label: 'PBNA', value: 456 },
      { label: 'Serbia', value: 71 }
    ]
  },
  {
    monthYear: 'Aug 2025',
    values: [
      { label: 'Benelux', value: 271 }
    ]
  }
];

// ChartWidget (Month-on-month Growth)
const momGrowthData = {
	"2024-12": 482,
	"2025-03": 779,
	"2025-06": 1427,
	"2025-08": 1698,
};

export default function IbpPipelines() {
	const [marketWiseChartData, setMarketWiseChartData] = useState<any[]>([]);
	const [marketWiseChartOptions, setMarketWiseChartOptions] = useState<any>({});
	const [marketWiseChartLoading, setMarketWiseChartLoading] = useState(true);
	const [ibpMomChartOptions, setIbpMomChartOptions] = useState<any>();
	const [ibpMomChartLoading, setIbpMomChartLoading] = useState(true);

	function getIbpMarketWiseChartDetails() {
		if(marketWiseData) {
			setMarketWiseChartLoading(true);

			const barData: any[] = [];
			const allLabelsSet = new Set<string>();

			marketWiseData.forEach(item => {
				const { monthYear, values } = item;
				values.forEach(({ label }) => allLabelsSet.add(label));
				barData.push({ monthYear, values });
			});

			const labelOrder = ['Benelux', 'FLNA', 'France', 'PBNA', 'Turkey', 'Serbia'];
			const uniqueLabels = Array.from(allLabelsSet);

			const sortedLabels = [
				...labelOrder.filter(label => uniqueLabels.includes(label)),
				...uniqueLabels.filter(label => !labelOrder.includes(label))
			];

			const xAxisLabels = barData.map(item => item.monthYear);

			const series = sortedLabels.map((label, index) => ({
				name: label,
				type: 'bar',
				data: barData.map(item =>
					item.values.find((v: any) => v.label === label)?.value || 0
				),
				itemStyle: {
					color: marketWiseColorMap[label] || ['#8884d8', '#82ca9d', '#ffc658'][index % 3]
				},
				label: {
					show: true,
					position: 'top',
					fontSize: 12,
					color: '#FFF',
					formatter: function(params: any) {
					return params.value === 0 ? '' : params.value;
					}
				},
				barWidth: barData.length === 1 ? '8%' : '14%'
			}));

			const maxValue = Math.max(...barData.flatMap(item => item.values.map((v: any) => v.value)));
			const stepSize = maxValue > 1000 ? 500 : 100;
			const maxYAxis = Math.ceil(maxValue / stepSize) * stepSize;

			const options = {
				textStyle: { color: '#A7ADCB' },
				backgroundColor: '#003771',
				tooltip: {
					trigger: 'axis',
					formatter: function(params: any) {
					const header = params[0]?.axisValueLabel || '';
					const rows = params
						.filter((p: any) => p.value !== 0)
						.map((p: any) =>
						`<span style="display:inline-block;margin-right:5px;border-radius:2px;width:10px;height:10px;background:${p.color};"></span>
						${p.seriesName}: <b>${p.value}</b>`
						);
					return `<div>${header}</div>${rows.join('<br/>')}`;
					}
				},
				legend: {
					data: sortedLabels,
					bottom: 0,
					textStyle: { color: '#A7ADCB' },
					type: 'scroll',
					pageIconColor: '#A7ADCB',
					pageIconInactiveColor: '#444C63',
					pageTextStyle: { color: '#A7ADCB', fontSize: 14 },
					pageButtonItemGap: 5,
					pageButtonGap: 10,
				},
				grid: {
					left: '5%',
					right: '5%',
					bottom: '10%',
					containLabel: true
				},
				xAxis: [
					{
					type: 'category',
					data: xAxisLabels,
					axisTick: { show: false },
					axisLine: { show: true },
					axisLabel: { color: '#A7ADCB', fontSize: 14 }
					}
				],
				yAxis: [
					{
					type: 'value',
					min: 0,
					max: maxYAxis,
					interval: stepSize,
					axisLabel: { formatter: '{value}', color: '#A7ADCB', fontSize: 14 },
					splitLine: { show: false }
					}
				],
				series
			};
			setMarketWiseChartData(barData);
			setMarketWiseChartOptions(options);
			setMarketWiseChartLoading(false);
		}
		if(momGrowthData) {
			setIbpMomChartLoading(true);
			setIbpMomChartOptions(momGrowthData);
			setIbpMomChartLoading(false);
		}
	}

	useEffect(() => {
		getIbpMarketWiseChartDetails();
	}, [])
	
  return (
    <PageWrapper sx={() => ({ minHeight: '100vh'})}>
      <PageHeader title="IBP Pipelines" icon={IBPExecutiveDashboardIcon}></PageHeader>
      <Grid container spacing={2}>
        {/* Left Side - IBP Pipelines Market wise chart */}
        <Grid item xs={12} md={6} id="ibp-market-wise">
          <SegmentedBarChart
            title="IBP Pipelines by Markets"
            data={marketWiseChartData}
            options={marketWiseChartOptions}
            isLoading={marketWiseChartLoading}
          />
        </Grid>
        {/* Right Side - IBP Pipeline Month-on-month Growth */}
        <Grid item xs={12} md={6} id="ibp-mom-growth">
          <ChartWidget
            title="IBP Pipeline Month-on-Month Growth"
            data={ibpMomChartOptions}
            isLoading={ibpMomChartLoading}
			minTitleHeight="40px"
			hasNoFormat
          />
        </Grid>
      </Grid>
    </PageWrapper>
  );
}